import { useEffect, useState } from "react"
import { Button, Pagination, Table } from "react-bootstrap"
import DeleteModal from "./DeleteModal"
import AddUserModal from "./AddUserModal"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { deleteUser, getUsers, inviteUser } from "../../../data/userSlice"
import { formatDate } from "../../../constant/utils"
import { css } from "@emotion/react"
import Loader from "../../../components/Loader"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const componentStyle = css`
    h2 {
        color: #fff;
    }

    .pagination {
        display: flex;
        justify-content: end;
    }

    .pagination .page-item.active .page-link {
        background-color: #0b274b;
    }

    .pagination .page-item .page-link {
        border-radius: 0 !important;
    }
`

const Users = () => {
    const dispatch = useDispatch()

    const [showModal, setShowModal] = useState(false)
    const [currentUser, setCurrentUser] = useState()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [activePage, setActivePage] = useState(0)

    const { data: users, fetchStatus, totalRecords } = useSelector((state) => state.user)
    const { role } = useSelector((state) => state.authentication)

    const numberOfRecordsPerPage = 25
    const totalPages = Math.ceil(totalRecords / numberOfRecordsPerPage)

    useEffect(() => {
        getUsersList()
    }, [])

    const getUsersList = (pageIndex = 1) => {
        return dispatch(
            getUsers({
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstName",
                descending: false
            })
        )
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentUser({})
    }

    const handleEditUser = (user) => {
        setCurrentUser(user)
        setShowModal(true)
    }

    const handleDeleteUser = (user) => {
        setCurrentUser(user)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = () => {
        dispatch(deleteUser(currentUser?.id))
        setShowDeleteModal(false)
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
    }

    // Function to handle page change
    const handlePageChange = (index) => {
        if (index >= 0) {
            if (index === activePage) return
            setActivePage(index)
            getUsersList(index + 1)
        }
    }

    const handleInvitation = (userId) => {
        if (userId) {
            dispatch(inviteUser(userId))
        }
    }

    const renderPagination = () => {
        const paginationItems = []
        const ellipsis = (
            <Pagination.Ellipsis
                key="ellipsis"
                disabled
            />
        )
        const pagesToShow = 5 // Number of pages to show in the middle
        const tailPagesToShow = 2 // Number of pages to show at the end

        // Add First and Previous Buttons
        paginationItems.push(
            <Pagination.First
                key="first"
                onClick={() => handlePageChange(0)}
                disabled={activePage === 0}
            />
        )
        paginationItems.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 0}
            />
        )

        if (totalPages <= pagesToShow + tailPagesToShow) {
            // If total pages are small, show all pages
            for (let i = 0; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        } else {
            // Show first page
            paginationItems.push(
                <Pagination.Item
                    key={0}
                    onClick={() => handlePageChange(0)}
                    active={activePage === 0}>
                    {1}
                </Pagination.Item>
            )

            // Determine start and end of the middle range
            const startPage = Math.max(1, activePage - Math.floor((pagesToShow - 1) / 2))
            const endPage = Math.min(totalPages - tailPagesToShow, startPage + pagesToShow - 1)

            // Add ellipsis before middle pages if needed
            if (startPage > 1) {
                paginationItems.push(ellipsis)
            }

            // Add middle pages
            for (let i = startPage; i < endPage; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }

            // Add ellipsis after middle pages if needed
            if (endPage < totalPages - tailPagesToShow) {
                paginationItems.push(ellipsis)
            }

            // Show last few pages
            for (let i = totalPages - tailPagesToShow; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        }

        // Add Next and Last Buttons
        paginationItems.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        paginationItems.push(
            <Pagination.Last
                key="last"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={activePage === totalPages - 1}
            />
        )

        return paginationItems
    }

    return (
        <div
            css={componentStyle}
            className="page-container"
            style={{ marginTop: "80px" }}>
            <Loader show={fetchStatus === "loading"} />
            <div className="d-flex justify-content-between align-items-center my-2">
                <h2>GAI Users</h2>
                {role !== "Standard" && (
                    <Button
                        className="btn-sm"
                        variant="light"
                        onClick={() => {
                            setCurrentUser({})
                            setShowModal(true)
                        }}>
                        Add User
                    </Button>
                )}
            </div>

            <AddUserModal
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                getUsersList={getUsersList}
                fetchStatus={fetchStatus}
                setActivePage={setActivePage}
            />

            <DeleteModal
                showDeleteModal={showDeleteModal}
                handleCancelDelete={handleCancelDelete}
                currentUser={currentUser}
                handleConfirmDelete={handleConfirmDelete}
            />
            <div className="bg-white p-3">
                <Table
                    striped
                    bordered
                    hover
                    className="mt-1">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                            <th>Last Login</th>
                            <th className="text-center">Re-invite</th>
                            <th style={{ width: "80px", textAlign: "center" }}>Active</th>
                            {role !== "Standard" && <th style={{ width: "120px", textAlign: "center" }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {users?.map((user) => (
                            <tr
                                key={user?.id}
                                className="centered-row">
                                <td>{user?.firstName}</td>
                                <td>{user?.lastName}</td>
                                <td>{user?.email}</td>
                                <td>{formatDate(user?.lastLoginDateTimeUtc, true)}</td>
                                <td className="text-center">
                                    <Button
                                        variant="light"
                                        className="btn-sm"
                                        onClick={() => handleInvitation(user?.id)}
                                        disabled={user?.hasPassword}>
                                        <FontAwesomeIcon
                                            icon={byPrefixAndName.fal["envelope"]}
                                            style={{ color: "#000000" }}
                                        />
                                    </Button>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                    {user?.active && <FontAwesomeIcon icon={byPrefixAndName.fal["check"]} />}
                                </td>
                                {role !== "Standard" && (
                                    <td style={{ textAlign: "center" }}>
                                        <>
                                            <Button
                                                variant="light"
                                                title="Edit"
                                                className="me-2 btn-sm"
                                                onClick={() => handleEditUser(user)}>
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["pen-to-square"]} />
                                            </Button>
                                            <Button
                                                className="btn-sm"
                                                variant="light"
                                                title="Deactivate"
                                                onClick={() => handleDeleteUser(user)}>
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["user-xmark"]} />
                                            </Button>
                                        </>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {totalPages > 1 && (
                    <Pagination
                        className="mt-4 d-flex justify-content-end"
                        size="md">
                        {renderPagination()}
                    </Pagination>
                )}
            </div>
        </div>
    )
}

export default Users
